import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lightbulb, SkipForward, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DIFF_COLORS = { debutant: 'text-[#06d6a0]', intermediaire: 'text-[#ffd93d]', expert: 'text-[#ff6b6b]', adaptatif: 'text-purple-400' };

function TimerBar({ timer, timeLeft }) {
  if (timer === 0) return null;
  const pct = timer > 0 ? (timeLeft / timer) * 100 : 100;
  const color = pct > 50 ? '#06d6a0' : pct > 25 ? '#ffd93d' : '#ff6b6b';
  return (
    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
      <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function QuizSolo() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('intro'); // intro, question, result, skip_review, finished
  const [currentIdx, setCurrentIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState([]); // AttemptAnswer[]
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [hintUsed, setHintUsed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [questionStart, setQuestionStart] = useState(null);
  const timerRef = useRef(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [skippedQueue, setSkippedQueue] = useState([]); // indices of skipped questions still to review
  const submitAnswerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/quiz/${quizId}`, { withCredentials: true })
      .then(res => { setQuiz(res.data); setLoading(false); })
      .catch(() => navigate('/quiz'));
  }, [quizId, navigate]);

  const currentQ = quiz?.questions?.[currentIdx];
  const config = quiz?.config || {};
  const timerVal = currentQ?.timer || config.timer_global || 20;

  const goToNextQuestion = useCallback(() => {
    clearInterval(timerRef.current);
    const totalQs = quiz?.questions?.length || 0;

    // Find next question in skippedQueue or normal flow
    if (skippedQueue.length > 0) {
      // We're reviewing skipped questions
      const nextSkipped = skippedQueue[0];
      setSkippedQueue(prev => prev.slice(1));
      setCurrentIdx(nextSkipped);
      setSelectedAnswer(null);
      setTextAnswer('');
      setHintUsed(false);
      setShowHint(false);
      setPhase('question');
      setQuestionStart(Date.now());
    } else if (currentIdx >= totalQs - 1) {
      // Reached the end — check for any skipped answers
      setAnswers(prev => {
        const skippedIdxs = [];
        quiz.questions.forEach((q, i) => {
          const answered = prev.find(a => a.question_id === q.question_id);
          if (!answered) skippedIdxs.push(i);
        });
        if (skippedIdxs.length > 0) {
          setSkippedQueue(skippedIdxs);
          setPhase('skip_review');
        } else {
          setPhase('finished');
        }
        return prev;
      });
    } else {
      setCurrentIdx(prev => prev + 1);
      setSelectedAnswer(null);
      setTextAnswer('');
      setHintUsed(false);
      setShowHint(false);
      setPhase('question');
      setQuestionStart(Date.now());
    }
  }, [currentIdx, quiz, skippedQueue]);

  const submitAnswer = useCallback((answer, skipped = false) => {
    clearInterval(timerRef.current);
    const elapsed = questionStart ? Math.floor((Date.now() - questionStart) / 1000) : 0;
    const remaining = Math.max(0, timerVal - elapsed);

    setAnswers(prev => {
      // If this question was already answered (skipped before), replace it
      const existing = prev.findIndex(a => a.question_id === currentQ.question_id);
      const entry = {
        question_id: currentQ.question_id,
        answer: skipped ? null : answer,
        time_remaining: remaining,
        hint_used: hintUsed,
        skipped,
      };
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = entry;
        return updated;
      }
      return [...prev, entry];
    });

    setSelectedAnswer(skipped ? '__skip__' : answer);
    setPhase('result');

    if (config.show_explanation) {
      setTimeout(goToNextQuestion, 2500);
    } else {
      goToNextQuestion();
    }
  }, [currentQ, timerVal, hintUsed, questionStart, goToNextQuestion, config]);

  // Maintient la ref à jour pour que le timer utilise toujours la version fraîche
  useEffect(() => {
    submitAnswerRef.current = submitAnswer;
  }, [submitAnswer]);

  // Timer
  useEffect(() => {
    if (phase !== 'question' || timerVal === 0) return;
    setTimeLeft(timerVal);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Utilise la ref pour éviter la stale closure sur submitAnswer
          submitAnswerRef.current(null, false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, currentIdx, timerVal]);

  const startQuiz = () => {
    setStartTime(Date.now());
    setQuestionStart(Date.now());
    setPhase('question');
  };

  // Submit attempt when finished
  useEffect(() => {
    if (phase !== 'finished' || !quiz || answers.length === 0) return;
    const timeTaken = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    axios.post(`${API}/api/quiz/${quizId}/attempt`, { quiz_id: quizId, answers, time_taken: timeTaken }, { withCredentials: true })
      .then(res => { setResults(res.data); setScore(res.data.score); })
      .catch(() => toast.error(t('quiz.savingError')));
  }, [phase]);

  if (loading) return (
    <div className="min-h-screen bg-[#0f0e17] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!quiz) return null;

  const handleReviewSkipped = (wantsToReview) => {
    if (wantsToReview && skippedQueue.length > 0) {
      const firstIdx = skippedQueue[0];
      setSkippedQueue(prev => prev.slice(1));
      setCurrentIdx(firstIdx);
      setSelectedAnswer(null);
      setTextAnswer('');
      setHintUsed(false);
      setShowHint(false);
      setPhase('question');
      setQuestionStart(Date.now());
    } else {
      setSkippedQueue([]);
      setPhase('finished');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0e17]">
      {phase === 'intro' && <IntroScreen quiz={quiz} user={user} onStart={startQuiz} onBack={() => navigate('/quiz')} />}
      {(phase === 'question' || phase === 'result') && currentQ && (
        <QuestionScreen
          q={currentQ} idx={currentIdx} total={quiz.questions.length}
          timeLeft={timeLeft} timerVal={timerVal} phase={phase}
          config={config} score={score} streak={streak}
          selectedAnswer={selectedAnswer} hintUsed={hintUsed} showHint={showHint}
          textAnswer={textAnswer} setTextAnswer={setTextAnswer}
          onAnswer={submitAnswer}
          onHint={() => { setHintUsed(true); setShowHint(true); }}
          onSkip={() => submitAnswer(null, true)}
          onNext={goToNextQuestion}
        />
      )}
      {phase === 'skip_review' && (
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-sm w-full text-center kurso-card p-8 animate-fade-up">
            <div className="text-3xl mb-4">🔄</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('quiz.skippedReview')}</h2>
            <p className="text-[#a0a0ab] mb-6">
              {t('quiz.skippedReviewMsg', { count: skippedQueue.length + 1 })}
            </p>
            <div className="flex gap-3">
              <button
                data-testid="quiz-review-yes"
                onClick={() => handleReviewSkipped(true)}
                className="flex-1 bg-[#ff6b6b] text-white py-3 rounded-xl font-semibold hover:bg-[#ff5252] transition-colors"
              >
                {t('quiz.reviewYes')}
              </button>
              <button
                data-testid="quiz-review-no"
                onClick={() => handleReviewSkipped(false)}
                className="flex-1 bg-white/5 text-[#a0a0ab] py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
              >
                {t('quiz.reviewNo')}
              </button>
            </div>
          </div>
        </div>
      )}
      {phase === 'finished' && !results && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-[#a0a0ab]">{t('quiz.calculating')}</p>
          </div>
        </div>
      )}
      {phase === 'finished' && results && (
        <ResultsScreen quiz={quiz} results={results} onRetry={() => navigate(0)} onBack={() => navigate('/quiz')} onViewDetails={() => navigate(`/quiz/attempt/${results.attempt_id}`)} />
      )}
    </div>
  );
}

function IntroScreen({ quiz, user, onStart, onBack }) {
  const { t } = useTranslation();
  const diff = {
    debutant: t('quiz.diffDebutant'),
    intermediaire: t('quiz.diffIntermediaire'),
    expert: t('quiz.diffExpert'),
    adaptatif: t('quiz.diffAdaptatif'),
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <button onClick={onBack} className="text-[#a0a0ab] hover:text-white text-sm mb-6 flex items-center gap-1">
          {t('quiz.backLink')}
        </button>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
          <div className="text-5xl mb-3">🧠</div>
          <h1 className="text-xl font-heading font-bold text-white mb-1">{quiz.title}</h1>
          <p className="text-[#a0a0ab] text-sm mb-4">{quiz.topic}</p>
          <div className="flex justify-center gap-4 text-xs text-[#a0a0ab] mb-6">
            <span>{quiz.num_questions} {t('quiz.questionsCount')}</span>
            <span className={DIFF_COLORS[quiz.difficulty]}>{diff[quiz.difficulty] || quiz.difficulty}</span>
            <span>{quiz.estimated_duration} {t('quiz.minutes')}</span>
            <span>{quiz.config?.timer_global === 0 ? t('quiz.noTimer') : t('quiz.timerPerQuestion', { n: quiz.config?.timer_global })}</span>
          </div>
          {!user && <p className="text-xs text-[#ffd93d] mb-3">{t('quiz.loginToSave')}</p>}
          <button onClick={onStart} className="btn-primary w-full py-3 text-base" data-testid="start-quiz-btn">
            {t('quiz.startBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuestionScreen({ q, idx, total, timeLeft, timerVal, phase, config, score, streak, selectedAnswer, hintUsed, showHint, textAnswer, setTextAnswer, onAnswer, onHint, onSkip, onNext }) {
  const { t } = useTranslation();
  const isResult = phase === 'result';
  const correct = q.correct_answers || [];

  const getOptionClass = (i) => {
    if (!isResult) return 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-[#ff6b6b]/30 text-white cursor-pointer';
    if (correct.includes(i)) return 'bg-[#06d6a0]/20 border-[#06d6a0] text-[#06d6a0]';
    if (selectedAnswer === i) return 'bg-[#ff6b6b]/20 border-[#ff6b6b] text-[#ff6b6b]';
    return 'bg-white/5 border-white/10 text-[#a0a0ab]';
  };

  return (
    <div className="min-h-screen px-4 py-6 flex flex-col max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 text-sm text-[#a0a0ab]">
        <span>{idx + 1} / {total}</span>
        <span className="text-white font-bold text-lg">{score.toLocaleString()} pts</span>
        {streak >= 2 && <span className="text-[#ffd93d] text-xs">{streak}x 🔥</span>}
      </div>

      <TimerBar timer={timerVal} timeLeft={timeLeft} />

      {/* Question */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4 flex-shrink-0">
        {q.emoji && <div className="text-3xl mb-2 text-center">{q.emoji}</div>}
        <p className="text-white text-base font-semibold text-center leading-snug">{q.text}</p>
        {isResult && config.show_correct_on_wrong !== false && (
          <div className={`mt-3 text-sm p-3 rounded-xl ${(Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer]).some(a => correct.includes(a)) ? 'bg-[#06d6a0]/10 text-[#06d6a0]' : 'bg-[#ff6b6b]/10 text-[#ff6b6b]'}`}>
            {(Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer]).some(a => correct.includes(a)) ? t('quiz.correctFeedback') : t('quiz.incorrectFeedback')}
          </div>
        )}
        {isResult && config.show_explanation && q.explanation && (
          <p className="text-[#a0a0ab] text-xs mt-2">{q.explanation}</p>
        )}
        {showHint && q.hint && (
          <div className="mt-2 p-2 bg-[#ffd93d]/10 border border-[#ffd93d]/30 rounded-lg text-xs text-[#ffd93d]">
            {t('quiz.hintLabel', { hint: q.hint })}
          </div>
        )}
      </div>

      {/* Options */}
      <div className="flex-1">
        {(q.type === 'qcm' || q.type === 'vrai_faux') && (
          <div className="grid grid-cols-1 gap-2">
            {(q.options || []).map((opt, i) => (
              <button key={i} disabled={isResult} onClick={() => onAnswer(i)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all text-sm ${getOptionClass(i)}`}
                data-testid={`answer-option-${i}`}>
                <span className="font-mono text-xs mr-2 opacity-60">{String.fromCharCode(65 + i)}.</span> {opt}
              </button>
            ))}
          </div>
        )}

        {q.type === 'choix_multiple' && <MultipleChoiceInput q={q} isResult={isResult} selectedAnswer={selectedAnswer} onAnswer={onAnswer} correct={correct} />}

        {(q.type === 'saisie_libre' || q.type === 'estimation') && (
          <div className="space-y-3">
            <input type={q.type === 'estimation' ? 'number' : 'text'} value={textAnswer} onChange={e => setTextAnswer(e.target.value)} disabled={isResult}
              placeholder={q.type === 'estimation' ? t('quiz.numberPlaceholder') : t('quiz.freeTextPlaceholder')}
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ff6b6b]/50 disabled:opacity-50"
              onKeyDown={e => { if (e.key === 'Enter' && !isResult && textAnswer.trim()) onAnswer(textAnswer.trim()); }} />
            {!isResult && (
              <button onClick={() => onAnswer(textAnswer.trim())} disabled={!textAnswer.trim()} className="w-full btn-primary py-3 text-sm disabled:opacity-50">
                {t('quiz.confirmBtn')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      {!isResult && (
        <div className="flex gap-2 mt-4">
          {config.allow_hints !== false && q.hint && !hintUsed && (
            <button onClick={onHint} className="flex items-center gap-1.5 text-xs text-[#ffd93d] bg-[#ffd93d]/10 border border-[#ffd93d]/30 px-3 py-2 rounded-xl hover:bg-[#ffd93d]/20 transition-colors">
              <Lightbulb size={12} /> {t('quiz.hintBtn')}
            </button>
          )}
          {config.allow_skip !== false && (
            <button onClick={onSkip} className="ml-auto flex items-center gap-1.5 text-xs text-[#a0a0ab] hover:text-white transition-colors">
              <SkipForward size={12} /> {t('quiz.skip')}
            </button>
          )}
        </div>
      )}
      {isResult && (
        <button onClick={onNext} className="mt-4 w-full btn-primary py-3 text-sm flex items-center justify-center gap-2" data-testid="next-question-btn">
          {idx + 1 >= total - 1 ? t('quiz.viewResults') : t('quiz.nextQuestion')} <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

function MultipleChoiceInput({ q, isResult, selectedAnswer, onAnswer, correct }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const toggle = (i) => {
    if (isResult) return;
    setSelected(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  };
  const getClass = (i) => {
    if (!isResult) return selected.includes(i) ? 'bg-[#ff6b6b]/20 border-[#ff6b6b] text-[#ff6b6b]' : 'bg-white/5 border-white/10 text-white cursor-pointer hover:bg-white/10';
    if (correct.includes(i)) return 'bg-[#06d6a0]/20 border-[#06d6a0] text-[#06d6a0]';
    if (selected.includes(i)) return 'bg-[#ff6b6b]/20 border-[#ff6b6b] text-[#ff6b6b]';
    return 'bg-white/5 border-white/10 text-[#a0a0ab]';
  };
  return (
    <div className="space-y-2">
      {(q.options || []).map((opt, i) => (
        <button key={i} onClick={() => toggle(i)} className={`w-full text-left px-4 py-3 rounded-xl border transition-all text-sm ${getClass(i)}`}>
          <span className="font-mono text-xs mr-2 opacity-60">{String.fromCharCode(65 + i)}.</span> {opt}
        </button>
      ))}
      {!isResult && (
        <button onClick={() => onAnswer(selected)} disabled={selected.length === 0} className="w-full btn-primary py-3 text-sm mt-2 disabled:opacity-50">
          {t('quiz.validateBtn')}
        </button>
      )}
    </div>
  );
}

function ResultsScreen({ quiz, results, onRetry, onBack, onViewDetails }) {
  const { t } = useTranslation();
  const pct = results.percentage || 0;
  const emoji = pct === 100 ? '🏆' : pct >= 80 ? '🎯' : pct >= 60 ? '👍' : pct >= 40 ? '📚' : '💪';
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center mb-4">
          <div className="text-5xl mb-3">{emoji}</div>
          <h2 className="text-2xl font-heading font-bold text-white mb-1">{pct}%</h2>
          <p className="text-[#a0a0ab] text-sm mb-2">{results.correct_count} / {results.total_questions} {t('quiz.correctAnswers')}</p>
          <p className="text-xl font-bold text-[#ff6b6b] mb-3">{results.score?.toLocaleString()} pts</p>
          {results.best_streak >= 3 && <p className="text-[#ffd93d] text-sm mb-2">{t('quiz.bestStreak', { n: results.best_streak })}</p>}
          <div className="bg-[#06d6a0]/10 border border-[#06d6a0]/30 rounded-xl px-4 py-2 inline-block mb-4">
            <span className="text-[#06d6a0] font-bold">+{results.xp_earned} XP</span>
          </div>
          {results.new_badges?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[#ffd93d] mb-1">{t('quiz.newBadgesUnlocked')}</p>
              <div className="flex justify-center gap-2">
                {results.new_badges.map(b => <span key={b} className="text-xs bg-[#ffd93d]/10 text-[#ffd93d] px-2 py-1 rounded-full">{b}</span>)}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onBack} className="flex-1 bg-white/5 border border-white/10 text-white py-3 rounded-xl text-sm hover:bg-white/10">{t('quiz.quizzesList')}</button>
          <button onClick={onRetry} className="flex-1 btn-primary py-3 text-sm" data-testid="retry-quiz-btn">{t('quiz.retryQuiz')}</button>
        </div>
      </div>
    </div>
  );
}
